import { useState, useEffect, useCallback } from 'react';
import { recursiveFind } from '../../../utilities/treeHelpers';

const normalizeEntityName = (name) => {
  if (!name) return '';
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
};

const useSimilarEntityDetection = (groupedMeters, entityTree) => {
  const [similarEntityMatches, setSimilarEntityMatches] = useState({});

  const findEntityByName = (tree, entityName) => {
    if (!tree || !entityName) return null;
    return recursiveFind(tree, (node) => node.divName === entityName);
  };

  const findSimilarEntity = useCallback((tree, entityName) => {
    if (!tree || !entityName) return null;
    const normalizedSearch = normalizeEntityName(entityName);
    return recursiveFind(tree, (node) => normalizeEntityName(node.divName) === normalizedSearch);
  }, []);

  const detectSimilarEntities = useCallback(
    (groups) => {
      if (!groups || !entityTree) return {};

      const matches = {};
      const processedNames = new Set();

      groups.forEach((group) => {
        const entityName = group.entityName;
        if (processedNames.has(entityName)) return;
        processedNames.add(entityName);

        const exactMatch = findEntityByName(entityTree, entityName);

        if (exactMatch) {
          matches[entityName] = {
            similarEntity: exactMatch,
            action: null,
            isExactMatch: true
          };
          return;
        }

        const similarEntity = findSimilarEntity(entityTree, entityName);

        if (similarEntity) {
          matches[entityName] = {
            similarEntity: similarEntity,
            action: null,
            isExactMatch: false
          };
        }
      });

      return matches;
    },
    [entityTree, findSimilarEntity]
  );

  useEffect(() => {
    if (groupedMeters && entityTree) {
      const newMatches = detectSimilarEntities(groupedMeters);
      setSimilarEntityMatches((prev) => {
        const merged = { ...newMatches };
        Object.keys(newMatches).forEach((entityName) => {
          if (prev[entityName]?.action) {
            merged[entityName] = { ...newMatches[entityName], action: prev[entityName].action };
          }
        });
        return merged;
      });
    }
  }, [groupedMeters, entityTree, detectSimilarEntities]);

  const handleKeepExisting = useCallback((entityName) => {
    setSimilarEntityMatches((prev) => ({
      ...prev,
      [entityName]: { ...prev[entityName], action: 'keep' }
    }));
  }, []);

  const handleUseNew = useCallback((entityName) => {
    setSimilarEntityMatches((prev) => ({
      ...prev,
      [entityName]: { ...prev[entityName], action: 'use-new' }
    }));
  }, []);

  const renameEntityInMatches = useCallback((oldName, newName) => {
    setSimilarEntityMatches((prev) => {
      if (!prev[oldName]) return prev;
      const updated = { ...prev };
      updated[newName] = { ...prev[oldName] };
      delete updated[oldName];
      return updated;
    });
  }, []);

  const updateSimilarMatch = useCallback((entityName, selectedEntity) => {
    setSimilarEntityMatches((prev) => ({
      ...prev,
      [entityName]: {
        similarEntity: selectedEntity,
        action: 'keep',
        isExactMatch: false
      }
    }));
  }, []);

  return {
    similarEntityMatches,
    handleKeepExisting,
    handleUseNew,
    renameEntityInMatches,
    updateSimilarMatch
  };
};

export default useSimilarEntityDetection;
